import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const DEFAULT_BHK = ["2 BHK", "2.5 BHK", "3 BHK", "3.5 BHK", "4 BHK", "4.5 BHK", "5 BHK"];

const FALLBACK_AMENITIES = [
  "Grand Clubhouse",
  "Swimming Pool",
  "Fully Equipped Gym",
  "Children's Play Area",
  "Landscaped Garden",
  "Jogging & Walking Track",
  "Multipurpose Hall",
  "Indoor Games Room",
  "Badminton Court",
  "Basketball Court",
  "Yoga & Meditation Deck",
  "Amphitheatre",
  "Senior Citizen Seating Area",
  "Cricket Practice Net / Box Cricket",
  "Party Lawn & BBQ Area",
  "Co-working Space",
  "EV Charging Stations",
  "24×7 CCTV & Security",
  "Visitor Parking",
  "Pet Park",
];

const EMPTY = {
  project_name: "",
  developer_name: "",
  location: "",
  configurations: [],
  rera_possession: "",
  developer_commitment: "",
  total_towers: "",
  total_land_parcel: "",
  total_floors: "",
  comments: "",
};

export default function PropertyDialog({ open, onOpenChange, property }) {
  const qc = useQueryClient();
  const editing = Boolean(property?.id);

  const defsQ = useQuery({
    queryKey: ["property-defaults"],
    queryFn: async () => (await api.get("/properties/amenities")).data,
    staleTime: 60 * 60_000,
    enabled: open,
  });

  const bhkOptions = defsQ.data?.bhk_options || DEFAULT_BHK;
  const amenities = useMemo(
    () => property?.amenities || defsQ.data?.amenities || FALLBACK_AMENITIES,
    [property, defsQ.data]
  );

  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    if (property) {
      setForm({
        project_name: property.project_name || "",
        developer_name: property.developer_name || "",
        location: property.location || "",
        configurations: (property.configurations || []).map((c) => ({
          bhk: c.bhk,
          variants: (c.variants || []).map((v) => ({ sqft: v.sqft || "", price: v.price || "" })),
        })),
        rera_possession: property.rera_possession || "",
        developer_commitment: property.developer_commitment || "",
        total_towers: property.total_towers ?? "",
        total_land_parcel: property.total_land_parcel || "",
        total_floors: property.total_floors ?? "",
        comments: property.comments || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, property]);

  const patchField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const addConfig = (bhk) => {
    if (form.configurations.some((c) => c.bhk === bhk)) return;
    setForm((f) => ({
      ...f,
      configurations: [...f.configurations, { bhk, variants: [{ sqft: "", price: "" }] }],
    }));
  };

  const removeConfig = (bhk) => {
    setForm((f) => ({ ...f, configurations: f.configurations.filter((c) => c.bhk !== bhk) }));
  };

  const addVariant = (bhk) => {
    setForm((f) => ({
      ...f,
      configurations: f.configurations.map((c) =>
        c.bhk === bhk ? { ...c, variants: [...c.variants, { sqft: "", price: "" }] } : c
      ),
    }));
  };

  const updateVariant = (bhk, idx, key, val) => {
    setForm((f) => ({
      ...f,
      configurations: f.configurations.map((c) =>
        c.bhk === bhk
          ? {
              ...c,
              variants: c.variants.map((v, i) => (i === idx ? { ...v, [key]: val } : v)),
            }
          : c
      ),
    }));
  };

  const removeVariant = (bhk, idx) => {
    setForm((f) => ({
      ...f,
      configurations: f.configurations.map((c) =>
        c.bhk === bhk
          ? { ...c, variants: c.variants.filter((_, i) => i !== idx) }
          : c
      ),
    }));
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const toInt = (v) => {
        const n = parseInt(String(v).trim(), 10);
        return Number.isFinite(n) ? n : null;
      };
      const payload = {
        project_name: form.project_name.trim(),
        developer_name: form.developer_name.trim(),
        location: form.location.trim(),
        configurations: form.configurations
          .filter((c) => c.variants.some((v) => v.sqft || v.price))
          .map((c) => ({
            bhk: c.bhk,
            variants: c.variants.filter((v) => v.sqft || v.price),
          })),
        rera_possession: form.rera_possession.trim() || null,
        developer_commitment: form.developer_commitment.trim() || null,
        total_towers: form.total_towers === "" ? null : toInt(form.total_towers),
        total_land_parcel: form.total_land_parcel.trim() || null,
        total_floors: form.total_floors === "" ? null : toInt(form.total_floors),
        comments: form.comments.trim() || null,
      };
      if (editing) {
        const { data } = await api.patch(`/properties/${property.id}`, payload);
        return data;
      }
      const { data } = await api.post("/properties", payload);
      return data;
    },
    onSuccess: () => {
      toast.success(editing ? "Project updated" : "Project added");
      qc.invalidateQueries({ queryKey: ["properties"] });
      qc.invalidateQueries({ queryKey: ["project-locations"] });
      onOpenChange(false);
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Save failed"),
  });

  const canSubmit =
    form.project_name.trim() && form.developer_name.trim() && form.location.trim() && !saveMut.isPending;

  const availableBhk = bhkOptions.filter(
    (b) => !form.configurations.some((c) => c.bhk === b)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Project" : "Add Project"}</DialogTitle>
          <DialogDescription>
            Capture project details, configurations (sqft & price per BHK) and amenities.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>Project Name</Label>
              <Input
                data-testid="prop-project-name"
                value={form.project_name}
                onChange={(e) => patchField("project_name", e.target.value)}
                placeholder="e.g. Marvel Sangria"
              />
            </div>
            <div>
              <Label>Developer Name</Label>
              <Input
                data-testid="prop-developer-name"
                value={form.developer_name}
                onChange={(e) => patchField("developer_name", e.target.value)}
                placeholder="e.g. Marvel Realtors"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Location</Label>
              <Input
                data-testid="prop-location"
                value={form.location}
                onChange={(e) => patchField("location", e.target.value)}
                placeholder="e.g. NIBM Annexe, Pune"
              />
            </div>

            <div className="sm:col-span-2">
              <Label>RERA / Possession</Label>
              <Input
                data-testid="prop-rera-possession"
                value={form.rera_possession}
                onChange={(e) => patchField("rera_possession", e.target.value)}
                placeholder="e.g. P52100048291 · Possession Dec 2028"
              />
            </div>

            <div className="sm:col-span-2">
              <Label>Developer Commitment</Label>
              <textarea
                data-testid="prop-developer-commitment"
                value={form.developer_commitment}
                onChange={(e) => patchField("developer_commitment", e.target.value)}
                placeholder="e.g. On-time delivery guarantee · Assured rental for 2 years"
                rows={2}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
              />
            </div>

            <div>
              <Label>Total Towers</Label>
              <Input
                data-testid="prop-total-towers"
                type="number"
                inputMode="numeric"
                min="0"
                value={form.total_towers}
                onChange={(e) => patchField("total_towers", e.target.value)}
                placeholder="e.g. 5"
              />
            </div>
            <div>
              <Label>Total Floors (per tower)</Label>
              <Input
                data-testid="prop-total-floors"
                type="number"
                inputMode="numeric"
                min="0"
                value={form.total_floors}
                onChange={(e) => patchField("total_floors", e.target.value)}
                placeholder="e.g. 32"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Total Land Parcel</Label>
              <Input
                data-testid="prop-total-land-parcel"
                value={form.total_land_parcel}
                onChange={(e) => patchField("total_land_parcel", e.target.value)}
                placeholder="e.g. 12.5 acres"
              />
            </div>
          </div>

          {/* Configurations */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label>Configuration</Label>
              <div className="flex flex-wrap gap-1.5">
                {availableBhk.map((b) => (
                  <button
                    key={b}
                    type="button"
                    data-testid={`prop-add-bhk-${b.replace(/\s+/g, "-")}`}
                    onClick={() => addConfig(b)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
                  >
                    <Plus className="h-3 w-3" /> {b}
                  </button>
                ))}
              </div>
            </div>

            {form.configurations.length === 0 && (
              <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">
                Tap a BHK chip above to add configurations.
              </div>
            )}

            <div className="space-y-3">
              {form.configurations.map((c) => (
                <div key={c.bhk} className="rounded-xl border border-slate-200 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-bold text-navy">{c.bhk}</div>
                    <button
                      type="button"
                      data-testid={`prop-remove-bhk-${c.bhk.replace(/\s+/g, "-")}`}
                      onClick={() => removeConfig(c.bhk)}
                      className="grid h-7 w-7 place-items-center rounded-full hover:bg-rose-50"
                      aria-label={`Remove ${c.bhk}`}
                    >
                      <X className="h-3.5 w-3.5 text-rose-500" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {c.variants.map((v, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          data-testid={`prop-sqft-${c.bhk.replace(/\s+/g, "-")}-${i}`}
                          value={v.sqft}
                          onChange={(e) => updateVariant(c.bhk, i, "sqft", e.target.value)}
                          placeholder="Sqft (e.g. 1250)"
                          className="h-9"
                        />
                        <Input
                          data-testid={`prop-price-${c.bhk.replace(/\s+/g, "-")}-${i}`}
                          value={v.price}
                          onChange={(e) => updateVariant(c.bhk, i, "price", e.target.value)}
                          placeholder="Price (e.g. 1.25 Cr)"
                          className="h-9"
                        />
                        <button
                          type="button"
                          onClick={() => removeVariant(c.bhk, i)}
                          className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-slate-400 hover:bg-slate-100"
                          aria-label="Remove row"
                          disabled={c.variants.length <= 1}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      data-testid={`prop-add-variant-${c.bhk.replace(/\s+/g, "-")}`}
                      onClick={() => addVariant(c.bhk)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--brand-navy))] hover:underline"
                    >
                      <Plus className="h-3 w-3" /> Add sqft / price
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Amenities (always included, read-only) */}
          <div>
            <Label>Amenities <span className="ml-1 text-xs font-normal text-slate-400">(all included)</span></Label>
            <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {amenities.map((a) => (
                <div
                  key={a}
                  className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-[12px] text-slate-700"
                  data-testid={`prop-amenity-${a.replace(/\W+/g, "-")}`}
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  {a}
                </div>
              ))}
            </div>
          </div>

          {/* Comments */}
          <div>
            <Label>Comments</Label>
            <textarea
              data-testid="prop-comments"
              value={form.comments}
              onChange={(e) => patchField("comments", e.target.value)}
              placeholder="Anything else worth noting — internal notes, pricing quirks, promotions…"
              rows={3}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
            />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="prop-cancel">
            Cancel
          </Button>
          <Button
            data-testid="prop-submit"
            className="bg-navy hover:brightness-110"
            disabled={!canSubmit}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? "Saving…" : editing ? "Save changes" : "Add Project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
